import { Link } from '@tanstack/react-router';
import { CoinsIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { EmptyState } from '@/shared/components/EmptyState';
import { PageHeader } from '@/shared/components/PageHeader';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { UsageSkeleton } from './UsageSkeleton';
import { USAGE_PAGE_SECTION } from '../usageLayout';

import { isForbidden } from '../errors';
import { useWorkspaceUsage } from '../hooks/useWorkspaceUsage';
import { useDailySplit } from '../hooks/useDailySplit';
import {
  SPLIT_DIMENSIONS,
  isSplitDimension,
  splitOptions,
  type SplitDimension,
} from '../dailySplit';
import { DailySpendLazy } from './DailySpendLazy';
import { TokenDetails } from './TokenDetails';
import { MeterFooter } from './MeterFooter';
import { meterFooterParts } from '../meterFooter';
import { PeriodControls } from './PeriodControls';
import { SpendMeter } from './SpendMeter';
import { WhereItWent } from './WhereItWent';
import type { OverTimeView } from '../overTime';

export interface UsagePageProps {
  workspaceId: string;
  /** The period being read, from the address. Absent means the current one. */
  period?: string;
  onPeriodChange: (next: string | undefined) => void;
  /** The Over time view, from the address (spec 254). */
  view?: OverTimeView;
  onViewChange?: (next: OverTimeView) => void;
  /** What the Per day bars split by, from the address (spec 257). */
  split?: SplitDimension;
  onSplitChange?: (next: SplitDimension) => void;
}

/**
 * What this workspace consumed, and what it cost (spec 072 US1).
 *
 * **Read-only about money.** An owner sees the spend, the limit applied to it, and where
 * the two sit relative to each other — and can change none of it. Limits are
 * organization-scoped and set in the admin console by someone holding a different role,
 * so this page offers no control that would lead nowhere (FR-008, FR-034).
 *
 * Composition, top to bottom (spec 232): the ratio first because that is the question
 * the page is opened with, its footer carrying the forecast, the limit's renewal and
 * the period's tokens; then where the money went, the next question; then the shape
 * over the period. The incompleteness notice sits directly under the figures it
 * qualifies, as one line. Three cards of one padding and one heading shape, and
 * nothing loose between them.
 */
export function UsagePage({
  workspaceId,
  period,
  onPeriodChange,
  view = 'cumulative',
  onViewChange,
  split = 'workflow',
  onSplitChange,
}: UsagePageProps) {
  const usage = useWorkspaceUsage(workspaceId, period);
  // A split the period cannot draw (one model) falls back to the default.
  const options = usage.data ? splitOptions(usage.data) : SPLIT_DIMENSIONS;
  const shownSplit = options.includes(split) ? split : 'workflow';
  const daily = useDailySplit(
    workspaceId,
    period,
    shownSplit,
    view === 'per-day' && usage.isSuccess,
  );
  const showPlaceholder = usePendingDisplay(usage.isPending);

  // The frame renders while the figures load (spec 079 FR-005). Its title and
  // description are static; only the period stepper needs the response, so it is the one
  // thing withheld — a control that cannot yet say which period it is on would be worse
  // than no control.
  if (usage.isPending) {
    return (
      <section className={USAGE_PAGE_SECTION}>
        <PageHeader
          eyebrow="Workspace"
          title="Usage"
          description="What this workspace has consumed this period, and the spend limit it is held to."
        />
        {showPlaceholder ? <UsageSkeleton /> : null}
      </section>
    );
  }

  if (usage.isError) {
    // A refusal is the expected answer for every member who does not own this
    // workspace, so it is explained rather than thrown at an error boundary (FR-004).
    return (
      <EmptyState
        icon={CoinsIcon}
        title={
          isForbidden(usage.error)
            ? 'Usage is for the workspace owner'
            : 'Usage could not be read'
        }
        description={
          isForbidden(usage.error)
            ? 'Only this workspace’s owner can see what it is spending. Ask them, or your organization administrator, if you need these figures.'
            : 'These figures could not be read just now.'
        }
        /*
          Spec 074 FR-027, FR-028. `EmptyState` has documented an `action` prop —
          "the way on: retry, or the action that would fill the region" — since it was
          written, and no caller had ever passed one, so every failed read on these
          surfaces ended the conversation by naming an action it did not offer.

          Only on this branch. A refusal is an answer, and retrying cannot change it
          (FR-028) — offering a button that will fail identically is worse than
          offering none.
        */
        action={
          isForbidden(usage.error) ? undefined : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void usage.refetch()}
              disabled={usage.isFetching}
            >
              {usage.isFetching ? 'Trying…' : 'Try again'}
            </Button>
          )
        }
      />
    );
  }

  const summary = usage.data;
  const currency = summary.spend.currency ?? 'USD';

  return (
    // The page's own frame, matching `OverviewPage`: `PagePlaceholder` supplies it for
    // pages that use it, and a page that composes its own body has to supply it itself
    // or it renders flush against the shell.
    <section className={USAGE_PAGE_SECTION}>
      <PageHeader
        eyebrow="Workspace"
        title="Usage"
        description="What this workspace has consumed this period, and the spend limit it is held to."
        actions={
          // Under AI usage these stand in the toolbar under the tabs, where the
          // Models tab's do (spec 224 review).
          <PeriodControls
            period={summary.period}
            earliestPeriodKey={summary.earliest_period_key}
            onChange={onPeriodChange}
          />
        }
      />

      {summary.run_count === 0 ? (
        /*
          An empty period is stated, not drawn (FR-010's sibling case). Zero bars and a
          zero meter would read as a page that failed to load rather than a month in
          which nothing ran.
        */
        <EmptyState
          icon={CoinsIcon}
          description="No executions have completed in this workspace during this period, so there is nothing to report."
        />
      ) : (
        <>
          <SpendMeter
            spend={summary.spend}
            limit={summary.limit}
            period={summary.period}
            runCount={summary.run_count}
            // Where the per-run detail lives (FR-038), on the total line (spec 256).
            // A signpost, not a drill-down: every run already has its place, in its
            // own task's history, and two surfaces answering "what did this run cost"
            // is how they come to disagree.
            runsLink={
              <Link
                to="/workspaces/$workspaceId/tasks"
                params={{ workspaceId }}
                className="text-primary focus-visible:ring-ring touch-pad rounded-sm hover:underline focus-visible:ring-3 focus-visible:outline-none"
              >
                Runs are listed on each task
              </Link>
            }
            // Where the period is heading (spec 099 US1), when the limit renews
            // (spec 091) — the limit's own cadence, not the calendar month the stepper
            // is browsing — and the period's tokens (spec 232), as the meter's own
            // last line (spec 231). The split by kind waits behind Token details.
            footer={
              <>
                <MeterFooter
                  parts={meterFooterParts({
                    spend: summary.spend,
                    limit: summary.limit,
                    period: summary.period,
                    tokens: {
                      total: summary.totals.total_tokens,
                      runs: summary.run_count,
                    },
                  })}
                />
                <TokenDetails totals={summary.totals} />
              </>
            }
          />

          {/*
            The caveat sits on the meter's total line now (spec 253): the full-width
            notice card was louder than the figures it qualified.
          */}

          {/*
            Where the money went, a share bar per dimension (spec 255). Straight after
            the meter (spec 232): it answers what an owner asks next, "on what".
            Choosing a slice splits the Per day bars by its dimension (spec 257).
          */}
          <WhereItWent
            workspaceId={workspaceId}
            period={period}
            summary={summary}
            onSliceChosen={(dimension) => {
              if (isSplitDimension(dimension) && options.includes(dimension))
                onSplitChange?.(dimension);
            }}
          />

          {/*
            Then the period over time, which answers "are we on track" and "when"
            (spec 254): Cumulative against the limit, or Per day.
          */}
          <DailySpendLazy
            entries={summary.by_day}
            currency={currency}
            period={summary.period}
            limit={summary.limit}
            spend={summary.spend}
            view={view}
            onViewChange={onViewChange}
            split={{
              value: shownSplit,
              options: [...options],
              onChange: (next) => onSplitChange?.(next),
              read: daily.data,
            }}
          />
        </>
      )}
    </section>
  );
}
